import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';

type Phase = 'loading' | 'ready' | 'error' | 'done';

export function CoachInvitePage() {
  const { candidateUid } = useParams<{ candidateUid: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [phase, setPhase] = useState<Phase>('loading');
  const [candidateName, setCandidateName] = useState('');
  const [coachName, setCoachName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [doneMsg, setDoneMsg] = useState('');
  const [acting, setActing] = useState(false);
  const [showLoginLink, setShowLoginLink] = useState(false);

  useEffect(() => {
    if (!candidateUid || !token) {
      setErrorMsg('This invite link is missing required information.');
      setPhase('error');
      return;
    }
    httpsCallable(getFunctions(), 'getCoachInviteContext')({ candidateUid, token })
      .then((res: any) => {
        const { candidateName: cn, coachName: chn, status } = res.data;
        setCandidateName(cn ?? 'A candidate');
        setCoachName(chn ?? 'Coach');
        if (status !== 'pending') {
          setDoneMsg(status === 'accepted'
            ? 'You have already accepted this invitation.'
            : 'This invitation has already been declined.');
          setPhase('done');
        } else {
          setPhase('ready');
        }
      })
      .catch(() => {
        setErrorMsg('This invite link is invalid or has expired.');
        setPhase('error');
      });
  }, [candidateUid, token]);

  const handleAction = async (action: 'accept' | 'decline') => {
    setActing(true);
    try {
      await httpsCallable(getFunctions(), 'handleCoachInviteResponse')({ candidateUid, token, action });
      setDoneMsg(action === 'accept'
        ? `You've accepted ${candidateName}'s invitation. They've been added to your caseload.`
        : `You've declined the invitation. No further action is needed.`);
      if (action === 'accept') setShowLoginLink(true);
      setPhase('done');
    } catch {
      setErrorMsg('Something went wrong. Please try again or contact support.');
      setPhase('error');
    } finally {
      setActing(false);
    }
  };

  return (
    <div style={outerStyle}>
      <div className="card" style={{ width: 420, textAlign: 'center' }}>
        <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '0.04em', color: 'var(--accent-text)', marginBottom: 6 }}>
          KLARR
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 32 }}>
          Coach Invitation
        </div>

        {phase === 'loading' && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading…</div>
        )}

        {phase === 'error' && (
          <div className="error-message">{errorMsg}</div>
        )}

        {phase === 'ready' && (
          <>
            <p style={{ fontSize: 15, color: 'var(--text-primary)', marginBottom: 8, lineHeight: 1.6 }}>
              <strong>{candidateName}</strong> has invited you to be their Klarr coach.
            </p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 28 }}>
              Would you like to accept or decline?
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                className="btn-primary"
                disabled={acting}
                onClick={() => handleAction('accept')}
                style={{ minWidth: 120, padding: '11px 20px', fontSize: 14 }}
              >
                {acting ? '…' : 'Accept'}
              </button>
              <button
                className="btn-secondary"
                disabled={acting}
                onClick={() => handleAction('decline')}
                style={{ minWidth: 120, padding: '11px 20px', fontSize: 14 }}
              >
                {acting ? '…' : 'Decline'}
              </button>
            </div>
          </>
        )}

        {phase === 'done' && (
          <>
            <p style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.7 }}>{doneMsg}</p>
            {showLoginLink && (
              <a
                href="https://admin.klarr.ai/login"
                style={{ display: 'inline-block', marginTop: 16, fontSize: 14, color: 'var(--accent-text)' }}
              >
                Log in to your Klarr portal →
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const outerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg-primary)',
};
